import {
  ChevronRight,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  Import,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
} from "lucide-react";
import { useMemo, useState } from "react";

import type { NoteListItem } from "@/hooks/useNotebook";
import { cn } from "@/lib/utils";
import {
  buildFolderTree,
  type FolderNode,
  isWithinFolder,
  parentFolderOf,
} from "@domain/notebook-folders";

interface NotebookDocListProps {
  notes: NoteListItem[];
  selectedPath: string | null;
  query: string;
  onQueryChange: (query: string) => void;
  onSelect: (path: string) => void;
  /** Called with the destination folder ("" = root) so "new note here" works. */
  onNewNote: (folder?: string) => void;
  onRefresh: () => void;
  isLoading?: boolean;
  /** Timestamp (ms) of the last successful list load, or null before the first. */
  lastRefreshedAt?: number | null;
  /** Ancestor-complete folder paths (note-derived ∪ ephemeral) — the tree. */
  folders?: string[];
  onMoveNote?: (args: { path: string; folder: string }) => void;
  /** Returns the created path, or null when the name was rejected — the
   *  input stays open on null so the user can fix it. */
  onCreateFolder?: (path: string) => string | null;
  onRenameFolder?: (args: { path: string; newPath: string }) => void;
  onDeleteFolder?: (path: string) => void;
  /** Opens the markdown import flow — omit to hide the affordance. */
  onImport?: () => void;
  className?: string;
}

/** Note title = filename (last path segment minus `.md`). Identity is the path;
 *  frontmatter title is display-only and not carried in the metadata-only list. */
function titleFromListPath(path: string): string {
  const last = path.split("/").pop() ?? path;
  return last.replace(/\.md$/i, "") || path;
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return "";
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return "";
  const mins = Math.floor((Date.now() - ts) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function formatRefreshed(ms: number | null | undefined): string {
  if (!ms) return "";
  const secs = Math.floor((Date.now() - ms) / 1000);
  if (secs < 5) return "just now";
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

/** Left column of the Notebook — folder tree while browsing (folders derived
 *  from note paths, §D1), flat results while searching. Notes are keyed by
 *  path (§4.3) and drag onto folders; folders manage inline. */
export function NotebookDocList({
  notes,
  selectedPath,
  query,
  onQueryChange,
  onSelect,
  onNewNote,
  onRefresh,
  isLoading = false,
  lastRefreshedAt,
  folders = [],
  onMoveNote,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onImport,
  className,
}: NotebookDocListProps) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [draggingPath, setDraggingPath] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [creatingIn, setCreatingIn] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [menuFor, setMenuFor] = useState<string | null>(null);

  const searching = query.trim().length > 0;
  // Note-derived folders merge in even when the caller passes none (e.g. the
  // quick view) — a nested note must always have a tree row to live under.
  const tree = useMemo(
    () => buildFolderTree(folders, notes.map((n) => parentFolderOf(n.path))),
    [folders, notes],
  );
  const rootNotes = useMemo(
    () => notes.filter((n) => parentFolderOf(n.path) === ""),
    [notes],
  );
  const canOrganize = Boolean(onCreateFolder);

  function toggleFolder(path: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }

  function handleDrop(folder: string) {
    if (draggingPath && onMoveNote) onMoveNote({ path: draggingPath, folder });
    setDraggingPath(null);
    setDropTarget(null);
  }

  function folderDropHandlers(path: string) {
    if (!onMoveNote) return {};
    return {
      onDragOver: (e: React.DragEvent) => {
        e.preventDefault();
        setDropTarget(path);
      },
      onDragLeave: () => setDropTarget((t) => (t === path ? null : t)),
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        handleDrop(path);
      },
    };
  }

  function submitNewFolder(name: string) {
    const trimmed = name.trim();
    if (!trimmed || !onCreateFolder) {
      setCreatingIn(null);
      return;
    }
    const created = onCreateFolder(creatingIn ? `${creatingIn}/${trimmed}` : trimmed);
    // A rejected name keeps the input open (the error copy renders upstream).
    if (created !== null) setCreatingIn(null);
  }

  function submitRename(path: string, name: string) {
    const trimmed = name.trim();
    const parent = parentFolderOf(path);
    if (trimmed && onRenameFolder && trimmed !== path.split("/").pop()) {
      onRenameFolder({ path, newPath: parent ? `${parent}/${trimmed}` : trimmed });
    }
    setRenaming(null);
  }

  function renderNoteRow(note: NoteListItem, depth: number) {
    return (
      <button
        key={note.path}
        onClick={() => onSelect(note.path)}
        draggable={Boolean(onMoveNote)}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = "move";
          setDraggingPath(note.path);
        }}
        onDragEnd={() => {
          setDraggingPath(null);
          setDropTarget(null);
        }}
        style={depth > 0 ? { paddingLeft: `${16 + depth * 14}px` } : undefined}
        className={cn(
          "w-full border-b border-black/5 px-4 py-3 text-left transition-colors dark:border-white/5",
          selectedPath === note.path
            ? "bg-brand-coral/5"
            : "hover:bg-black/5 dark:hover:bg-white/5",
          draggingPath === note.path && "opacity-40",
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="min-w-0 truncate text-sm font-bold text-black dark:text-white">
            {titleFromListPath(note.path)}
          </span>
          <FileText
            className="h-3.5 w-3.5 shrink-0 text-black/30 dark:text-white/30"
            strokeWidth={2}
          />
        </div>
        <div className="mt-0.5 truncate text-xs text-black/50 dark:text-white/50">
          {note.path}
        </div>
        <div className="mt-1 flex items-center gap-2 text-[11px] text-black/40 dark:text-white/40">
          {formatRelativeTime(note.updatedAt)}
        </div>
      </button>
    );
  }

  function renderNewFolderInput(depth: number) {
    return (
      <div
        className="flex items-center gap-2 border-b border-black/5 py-2 pr-4 dark:border-white/5"
        style={{ paddingLeft: `${16 + depth * 14}px` }}
      >
        <Folder
          className="h-4 w-4 shrink-0 text-brand-coral-dark dark:text-brand-coral"
          strokeWidth={2.2}
        />
        <input
          autoFocus
          placeholder="Folder name"
          className="w-full rounded-lg border border-brand-coral/40 bg-transparent px-2 py-1 text-sm text-black outline-none placeholder:text-black/35 dark:text-white dark:placeholder:text-white/35"
          onKeyDown={(e) => {
            if (e.key === "Enter") submitNewFolder(e.currentTarget.value);
            if (e.key === "Escape") setCreatingIn(null);
          }}
          onBlur={(e) => submitNewFolder(e.currentTarget.value)}
        />
      </div>
    );
  }

  function renderFolder(node: FolderNode, depth: number) {
    const isOpen = !collapsed.has(node.path);
    const directNotes = notes.filter((n) => parentFolderOf(n.path) === node.path);
    const total = notes.filter((n) =>
      isWithinFolder(parentFolderOf(n.path), node.path),
    ).length;
    const isDropTarget = dropTarget === node.path;
    const isRenaming = renaming === node.path;

    return (
      <div key={node.path}>
        <div
          {...folderDropHandlers(node.path)}
          className={cn(
            "group relative flex items-center gap-1.5 border-b border-black/5 py-2 pr-2 transition-colors dark:border-white/5",
            isDropTarget
              ? "bg-brand-coral/10 ring-1 ring-inset ring-brand-coral/50"
              : "hover:bg-black/5 dark:hover:bg-white/5",
          )}
          style={{ paddingLeft: `${8 + depth * 14}px` }}
        >
          {/* While renaming, the input replaces the toggle button entirely —
              an <input> nested inside a <button> is invalid HTML and hostile
              to keyboard use (Enter/Space would fire the toggle). */}
          {isRenaming ? (
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              <ChevronRight
                className={cn(
                  "h-3.5 w-3.5 shrink-0 text-black/40 transition-transform dark:text-white/40",
                  isOpen && "rotate-90",
                )}
                strokeWidth={2.4}
              />
              <Folder
                className="h-4 w-4 shrink-0 text-brand-coral-dark dark:text-brand-coral"
                strokeWidth={2.2}
              />
              <input
                autoFocus
                defaultValue={node.name}
                aria-label={`Rename ${node.name}`}
                className="w-full rounded-lg border border-brand-coral/40 bg-transparent px-2 py-0.5 text-sm text-black outline-none dark:text-white"
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitRename(node.path, e.currentTarget.value);
                  if (e.key === "Escape") setRenaming(null);
                }}
                onBlur={(e) => submitRename(node.path, e.currentTarget.value)}
              />
            </div>
          ) : (
            <button
              onClick={() => toggleFolder(node.path)}
              aria-label={isOpen ? `Collapse ${node.name}` : `Expand ${node.name}`}
              aria-expanded={isOpen}
              className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
            >
              <ChevronRight
                className={cn(
                  "h-3.5 w-3.5 shrink-0 text-black/40 transition-transform dark:text-white/40",
                  isOpen && "rotate-90",
                )}
                strokeWidth={2.4}
              />
              {isOpen ? (
                <FolderOpen
                  className="h-4 w-4 shrink-0 text-brand-coral-dark dark:text-brand-coral"
                  strokeWidth={2.2}
                />
              ) : (
                <Folder
                  className="h-4 w-4 shrink-0 text-black/45 dark:text-white/45"
                  strokeWidth={2.2}
                />
              )}
              <span className="min-w-0 truncate text-sm font-bold text-black dark:text-white">
                {node.name}
              </span>
              <span className="shrink-0 rounded-full bg-black/5 px-1.5 py-0.5 text-[10px] font-bold text-black/45 dark:bg-white/10 dark:text-white/45">
                {total}
              </span>
            </button>
          )}

          {canOrganize && !isRenaming && (
            <button
              onClick={() => setMenuFor(menuFor === node.path ? null : node.path)}
              aria-label={`Folder actions for ${node.name}`}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-black/40 opacity-0 transition-all group-hover:opacity-100 hover:bg-black/10 dark:text-white/40 dark:hover:bg-white/10"
            >
              <MoreHorizontal className="h-4 w-4" strokeWidth={2.2} />
            </button>
          )}

          {menuFor === node.path && (
            <>
              <button
                aria-label="Close menu"
                className="fixed inset-0 z-20 cursor-default"
                onClick={() => setMenuFor(null)}
              />
              <div className="absolute right-2 top-9 z-30 w-44 overflow-hidden rounded-xl border border-black/10 bg-white py-1 shadow-2xl dark:border-white/10 dark:bg-surface-dark-raised">
                {[
                  {
                    label: "New note here",
                    action: () => onNewNote(node.path),
                  },
                  {
                    label: "New subfolder",
                    action: () => {
                      setCollapsed((prev) => {
                        const next = new Set(prev);
                        next.delete(node.path);
                        return next;
                      });
                      setCreatingIn(node.path);
                    },
                  },
                  { label: "Rename", action: () => setRenaming(node.path) },
                  {
                    label: "Delete folder",
                    action: () => onDeleteFolder?.(node.path),
                    danger: true,
                  },
                ].map((item) => (
                  <button
                    key={item.label}
                    onClick={() => {
                      setMenuFor(null);
                      item.action();
                    }}
                    className={cn(
                      "w-full px-3.5 py-2 text-left text-[13px] font-bold transition-colors",
                      item.danger
                        ? "text-red-600 hover:bg-red-500/10 dark:text-red-400"
                        : "text-black/70 hover:bg-black/5 dark:text-white/70 dark:hover:bg-white/5",
                    )}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {isOpen && (
          <div>
            {creatingIn === node.path && renderNewFolderInput(depth + 1)}
            {node.children.map((child) => renderFolder(child, depth + 1))}
            {directNotes.map((note) => renderNoteRow(note, depth + 1))}
            {node.children.length === 0 &&
              directNotes.length === 0 &&
              creatingIn !== node.path && (
                <p
                  className="border-b border-black/5 py-2 text-xs italic text-black/35 dark:border-white/5 dark:text-white/35"
                  style={{ paddingLeft: `${16 + (depth + 1) * 14}px` }}
                >
                  Empty — drag a note in (folders live through their notes; an
                  empty one lasts only this session)
                </p>
              )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "h-full w-full flex-col border-r border-black/10 dark:border-white/10 md:w-80 lg:w-96",
        className ?? "flex",
      )}
    >
      <div className="shrink-0 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-black dark:text-white">Notebook</h2>
          <div className="flex items-center gap-1.5">
            {onImport && (
              <button
                onClick={onImport}
                title="Import markdown"
                aria-label="Import markdown"
                className="flex h-9 w-9 items-center justify-center rounded-full text-black/45 transition-colors hover:bg-black/5 hover:text-black/70 dark:text-white/45 dark:hover:bg-white/10 dark:hover:text-white/70"
              >
                <Import className="h-[18px] w-[18px]" strokeWidth={2.2} />
              </button>
            )}
            {canOrganize && (
              <button
                onClick={() => setCreatingIn("")}
                title="New folder"
                aria-label="New folder"
                className="flex h-9 w-9 items-center justify-center rounded-full text-black/45 transition-colors hover:bg-black/5 hover:text-black/70 dark:text-white/45 dark:hover:bg-white/10 dark:hover:text-white/70"
              >
                <FolderPlus className="h-[18px] w-[18px]" strokeWidth={2.2} />
              </button>
            )}
            <button
              onClick={() => onNewNote()}
              className="flex h-9 items-center gap-1.5 rounded-full bg-home-yellow px-3.5 text-[13px] font-bold text-home-ink transition-transform hover:scale-[1.03] active:scale-95"
            >
              <Plus className="h-4 w-4" strokeWidth={2.6} />
              New note
            </button>
          </div>
        </div>

        {/* Visible staleness affordance — this surface serves seconds-stale
            data by design (spec §3.4, frontend-doctrine §11-inv-4 carve-out),
            so the user always sees how fresh the list is + can force a refetch. */}
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-black/45 dark:text-white/45">
          <span>Shared with your agent</span>
          <span aria-hidden>·</span>
          <button
            onClick={onRefresh}
            disabled={isLoading}
            className="flex items-center gap-1 rounded px-1 py-0.5 font-medium transition-colors hover:text-black/70 disabled:opacity-60 dark:hover:text-white/70"
            title="Refresh notes"
          >
            <RefreshCw
              className={cn("h-3 w-3", isLoading && "animate-spin")}
              strokeWidth={2.4}
            />
            {isLoading
              ? "Refreshing…"
              : lastRefreshedAt
                ? `Refreshed ${formatRefreshed(lastRefreshedAt)}`
                : "Refresh"}
          </button>
        </div>

        {/* Search across titles and paths only — the list is metadata-only, so
            content search is a known v1 regression vs the mockup (spec §4.3). */}
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-black/10 px-3 py-2 focus-within:border-black/25 dark:border-white/15 dark:focus-within:border-white/30">
          <Search className="h-4 w-4 shrink-0 text-black/35 dark:text-white/35" />
          <input
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search notes"
            className="w-full bg-transparent text-sm text-black outline-none placeholder:text-black/35 dark:text-white dark:placeholder:text-white/35"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {searching ? (
          <>
            {notes.length === 0 && (
              <p className="px-4 py-6 text-sm text-black/45 dark:text-white/45">
                Nothing matches that search.
              </p>
            )}
            {notes.map((note) => renderNoteRow(note, 0))}
          </>
        ) : (
          <>
            {creatingIn === "" && renderNewFolderInput(0)}
            {tree.map((node) => renderFolder(node, 0))}
            {rootNotes.map((note) => renderNoteRow(note, 0))}
            {notes.length === 0 && tree.length === 0 && creatingIn === null && (
              <p className="px-4 py-6 text-sm text-black/45 dark:text-white/45">
                No notes yet.
              </p>
            )}
            {/* Drop out of any folder — appears only mid-drag. */}
            {draggingPath && onMoveNote && (
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDropTarget("");
                }}
                onDragLeave={() => setDropTarget((t) => (t === "" ? null : t))}
                onDrop={(e) => {
                  e.preventDefault();
                  handleDrop("");
                }}
                className={cn(
                  "mx-4 mt-3 rounded-xl border-2 border-dashed py-3 text-center text-xs font-bold transition-colors",
                  dropTarget === ""
                    ? "border-brand-coral bg-brand-coral/10 text-brand-coral-dark dark:text-brand-coral"
                    : "border-black/15 text-black/40 dark:border-white/20 dark:text-white/40",
                )}
              >
                Drop here to move out of folders
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
